<template>
  
  <v-sheet>

    <v-divider class="my-4"></v-divider>

    <v-row no-gutters>

      <v-col cols="6"></v-col>

      <v-col 
        cols    = "3" 
        class   = "pr-2"
      >

        <!-- Sort Order -->
        <v-select
          dense
          color             = "accent"
          item-color        = "accent"
          hide-details      = "auto"
          prepend-icon      = "mdi-sort"
          :items            = "orderItems"
          :value            = "order"
          :disabled         = "!canSort"
          @input            = "onOrderBy($event);"
        >
        </v-select>

      </v-col>

      <v-col cols="3">

        <!-- Filter Text -->
        <v-text-field
          color             = "accent"
          clearable
          dense
          prepend-icon      = "mdi-filter"
          hide-details      = "auto"
          :value            = "filter"
          :disabled         = "!canFilter"
          @input            = "onFilterBy($event);"
        >
        </v-text-field>

      </v-col>

    </v-row>

    <v-divider class="my-4"></v-divider>

    <div class="file-list-container">

      <v-list
        ref   = "file_list"
        class = "file-list"
      >

        <!-- Drives -->
        <v-subheader 
          v-if    = "folders.length === 0 && files.length === 0"
          inset
        >
          Locations
        </v-subheader>

        <v-list-item
          v-for   = "drive in drives"
          :key    = "drive.name"
          @click  = "$emit('folder', drive.path);"
        >
          <v-list-item-avatar>
            <v-icon v-if="drive.type === 'HardDisk'">
              mdi-harddisk
            </v-icon>
            <v-icon v-else-if="drive.type === 'Network'">
              mdi-server-network
            </v-icon>
            <v-icon v-else-if="drive.type === 'Cloud'">
              mdi-cloud
            </v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title v-text="drive.name"></v-list-item-title>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn
              icon
              @click.stop    = "onFavoriteFolder(drive.path);"
            >
              <v-icon 
                v-if  = "favorites.includes(drive.path)" 
                color = "primary"
              >
                mdi-star
              </v-icon>
              <v-icon v-else>mdi-star-outline</v-icon>
            </v-btn>
          </v-list-item-action>

        </v-list-item>

        <!-- Folders -->
        <v-subheader 
          v-if    = "folders.length > 0 || files.length > 0"
          inset
        >
          Folders
        </v-subheader>

        <v-list-item
          v-if    = "folders.length > 0 || files.length > 0"
          @click  = "onGoUpOneFolder"
        >
          <v-list-item-avatar>
            <v-icon>mdi-arrow-up-left</v-icon>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title>..</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-for   = "folder in folders"
          :key    = "folder.name"
          @click  = "$emit('folder', folder.path);"
        >
          <v-list-item-avatar>
            <v-icon color="yellow">mdi-folder</v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title v-text="folder.name"></v-list-item-title>
            <v-list-item-subtitle>{{ folder.lastModified | formatDate }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>
            <v-btn
              icon
              @click.stop    = "onFavoriteFolder(folder.path);"
            >
              <v-icon 
                v-if  = "favorites.includes(folder.path)" 
                color = "primary"
              >
                mdi-star
              </v-icon>
              <v-icon v-else>mdi-star-outline</v-icon>
            </v-btn>
          </v-list-item-action>

        </v-list-item>

        <!-- Files -->
        <v-subheader 
          v-if  = "files.length > 0"
          inset
        >
          Files
        </v-subheader>

        <v-list-item
          v-for       = "file in files"
          :key        = "file.name"
          class       = "file-item"
          @click      = "onSelectFile(file);"
        >
          <v-list-item-avatar
            class = "file-avatar"
          >
            <v-icon color="primary">mdi-file</v-icon>
            <span class="extension">{{ file.name | formatExtension }}</span>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title v-text="file.name"></v-list-item-title>
            <v-list-item-subtitle>{{ file.lastModified | formatDate }}, {{ file.length | formatFileSize }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action>

            <!-- Custom Actions -->
            <v-tooltip
              v-for       = "action in fileActions"
              :key        = "action.command"
              bottom
              open-delay  = "500"
            >
              <template v-slot:activator="{ attrs, on }">
                <v-btn
                  v-bind      = "attrs"
                  v-on        = "on"
                  icon
                  @click.stop = "onInvokeAction(action, file);"
                >
                  <v-icon>{{ action.icon }}</v-icon>
                </v-btn>
              </template>
              <span>{{ action.tooltip }}</span>
            </v-tooltip>
            
            <!-- Multi-Select -->
            <v-btn
              icon
              @click.stop    = "onToggleSelection(file.path);"
            >
              <v-icon v-if="multiSelection.includes(file.path)">mdi-checkbox-marked</v-icon>
              <v-icon 
                v-else
                class   = "file-control"
              >
                mdi-checkbox-blank-outline
              </v-icon>

            </v-btn>
          </v-list-item-action>

        </v-list-item>

      </v-list>

      <v-toolbar
        v-if    = "multiSelection.length > 0"
        class   = "mt-4"
        rounded
      >
        {{ multiSelection.length }} File{{ multiSelection.length === 1 ? '' : 's' }} Selected
        <v-spacer></v-spacer>
        <v-btn
          color  = "primary"
          @click = "onSelectSelection"
        >
          Open
        </v-btn>
      </v-toolbar>

    </div>

  </v-sheet>

</template>

<script>

// #region Imports

/* RxJS */
import {Subject} from 'rxjs';
import {debounceTime} from 'rxjs/operators';

/* Telestream */
import {
  ORDER_FILES,
  FILTER_FILES,
  FAVORITE_FOLDER,
  UNFAVORITE_FOLDER
} from '@/utils/action-types';

// #endregion Imports

// #region Constants

const FILTER_INPUT_DEBOUNCE_MS = 500;

// #endregion Constants

export default {

  // #region Properties

  props: {
    path:        String,
    entries:     Array,
    order:       String,
    filter:      String,
    favorites:   Array,
    fileActions: Array
  },
  data() {
    return {
      orderItems: [
        {text: 'Name', value: 'name'},
        {text: 'Last Modified', value: 'last_modified'},
        {text: 'Size', value: 'size'}
      ],

      filterSubject:     new Subject(),
      filterSubcription: undefined,

      multiSelection: []    // Set would have been better, but are not reactive in vue,
    };
  },
  computed: {
    drives() {
      return this.entries.filter(e => e.type === 'HardDisk' || e.type === 'Network' || e.type === 'Cloud');
    },
    folders() {
      return this.entries.filter(e => e.type === 'folder')
        .sort((a, b) => {
          switch (this.order) {
          
          case 'last_modified': return b.lastModified - a.lastModified;
          default:
          case 'name': return a.name.localeCompare(b.name, undefined, {'sensitivity': 'base'});
          
          }
        });
    },
    files() {
      return this.entries
        .filter(e => this.extensionsFilter ? (e.type === 'file' && this.extensionsFilter.test(e.name)) : (e.type === 'file'))
        .sort((a, b) => {
          switch (this.order) {
          
          case 'last_modified': return b.lastModified - a.lastModified;
          case 'size': return b.length - a.length;
          default:
          case 'name': return a.name.localeCompare(b.name, undefined, {'sensitivity': 'base'});
          
          }
        });
    },
    canSort() {
      return !!this.path;
    },
    canFilter() {
      return !!this.path;
    },
  },
  watch: {
    path() {
      this.multiSelection = [];
      if (this.$refs.file_list) {
        this.$refs.file_list.$el.scrollTop = 0;
      }
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.filterSubscription = this.filterSubject
      .pipe(debounceTime(FILTER_INPUT_DEBOUNCE_MS))
      .subscribe((filter) => this.$store.dispatch(FILTER_FILES, filter));
  },

  destroyed() {
    if (this.filterSubcription) {
      this.filterSubscription.unsubscribe();
    }
  },

  // #endregion Life-Cycle

  // #region Filters

  filters: {
    formatDate(value) {
      return value.toLocaleString('en-US', {
        year:   'numeric',
        month:  'numeric',
        day:    'numeric',
        hour:   'numeric',
        minute: 'numeric',
        second: 'numeric',
      })
    },
    formatFileSize(bytes) {
      if (!bytes) {
        return '';
      }
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

      return (bytes / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + sizes[i];
    },
    formatExtension(fileName) {
      return `.${fileName.split('.').pop()}`;
    }
  },

  // #endregion Filters

  // #region Methods

  methods: {

    // #region Events

    onOrderBy(order) {
      this.$store.dispatch(ORDER_FILES, order);
    },

    onFilterBy(filter) {
      this.filterSubject.next(filter);
    },

    onGoUpOneFolder() {
      const dividerIndex = this.getIndexOfLastPathDivider(this.path);
      let newPath = undefined;
      if (dividerIndex >= 0) {
        newPath = this.path.slice(0, dividerIndex);
      }

      this.$emit('folder', newPath);
    },

    onFavoriteFolder(folder) {
      if (this.favorites.includes(folder)) {
        this.$store.dispatch(UNFAVORITE_FOLDER, folder);
      } else {
        this.$store.dispatch(FAVORITE_FOLDER, folder);
      }
    },

    onSelectFile(file) {
      this.$emit('files', [file.path]);
    },

    onSelectSelection() {
      this.$emit('files', this.multiSelection);
    },

    onToggleSelection(file) {
      if (this.multiSelection.includes(file)) {
        this.multiSelection = this.multiSelection.filter(f => f !== file); 
      } else {
        this.multiSelection.push(file);
      }
    },

    onInvokeAction(action, file) {
      this.$emit(
        'action', 
        {
          action: action,
          file:   file
        }
      );
    },

    // #endregion Events

    // #region Utilities

    getIndexOfLastPathDivider(path) {
      let indexOf = path.lastIndexOf('\\');

      if (indexOf < 0) indexOf = path.lastIndexOf('/');

      if (indexOf > 0 && path[indexOf - 1] == ':') indexOf++;

      return indexOf;
    }

    // #endregion Utilities

  }

  // #endregion Methods

}

</script>

<style scoped lang="scss">

.file-list-container {
  height:         65vh;
  display:        flex;
  flex-direction: column;

  .file-list {
    flex:       1;
    overflow:   auto;

    .file-item {
      .file-control {
        color: rgba(white, 0.3);
      }
    }

    .file-item:hover {
      .file-control {
        color: white;
      }
    }
  }

  .v-toolbar {
    flex:       0;
  }
}

.file-avatar {
  margin-top:     4px !important;
  position:       relative;

  .v-icon {
    margin-top:    -8px !important;
  }

  .extension {
    position:       absolute;
    bottom:         0px;
    color:        #bcbcbc;
    font-size:      11px;
  }
}

.v-list-item__action {
  flex-direction: row;
}

</style>